import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  Popover,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ButtonBase,
  Divider,
  Theme,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import countryData from "../../Common/data/country-list";

export interface CountryItem {
  id: number;
  flagImg: string;
  countryName: string;
  countryCode: string; // e.g. "+91"
}

// Popular / common countries to pin at the top of the search list
const POPULAR_COUNTRY_NAMES = [
  "India",
  "United States of America",
  "United Kingdom",
  "United Arab Emirates",
  "Canada",
  "Australia",
  "Saudi Arabia",
  "Singapore",
  "Germany",
  "Qatar",
  "Kuwait",
  "Oman",
];

interface CountryCodePhoneInputProps {
  value?: string | number;
  onChange?: (event: any) => void;
  onBlur?: (e: any) => void;
  name?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
  size?: "small" | "medium";
}

export const CountryCodePhoneInput: React.FC<CountryCodePhoneInputProps> = ({
  value = "",
  onChange,
  onBlur,
  name = "whatsappNumber",
  placeholder = "98765 43210",
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  size = "small",
}) => {
  const theme: Theme = useTheme();

  // Normalize country list
  const normalizedCountries: CountryItem[] = useMemo(() => {
    return countryData.map((c) => ({
      ...c,
      countryCode: c.countryCode.startsWith("+")
        ? c.countryCode
        : `+${c.countryCode}`,
    }));
  }, []);

  // Default to India (+91)
  const defaultCountry = useMemo(() => {
    return (
      normalizedCountries.find(
        (c) => c.countryCode === "+91" && c.countryName.toLowerCase() === "india"
      ) ||
      normalizedCountries.find((c) => c.countryCode === "+91") ||
      normalizedCountries[0]
    );
  }, [normalizedCountries]);

  const [selectedCountry, setSelectedCountry] = useState<CountryItem>(defaultCountry);
  const [nationalNumber, setNationalNumber] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const isInternalChange = useRef(false);

  // Parse external value into country + national number
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    const strVal =
      value !== undefined && value !== null ? String(value).trim() : "";

    if (!strVal) {
      setNationalNumber("");
      return;
    }

    const cleanVal = strVal;

    // 1. If starts with '+'
    if (cleanVal.startsWith("+")) {
      // Find longest matching country code
      const sortedByCodeLength = [...normalizedCountries].sort(
        (a, b) => b.countryCode.length - a.countryCode.length
      );
      const match = sortedByCodeLength.find((c) => cleanVal.startsWith(c.countryCode));
      if (match) {
        setSelectedCountry(match);
        const remaining = cleanVal.slice(match.countryCode.length).replace(/[^0-9]/g, "");
        setNationalNumber(remaining);
        return;
      }
    }

    const digitsOnly = cleanVal.replace(/[^0-9]/g, "");

    // 2. If it's a 10-digit number (standard Indian phone without country code)
    if (digitsOnly.length === 10) {
      setSelectedCountry(defaultCountry);
      setNationalNumber(digitsOnly);
      return;
    }

    // 3. If starts with 91 and has 12 digits (India with 91 prefix)
    if (digitsOnly.length === 12 && digitsOnly.startsWith("91")) {
      setSelectedCountry(defaultCountry);
      setNationalNumber(digitsOnly.slice(2));
      return;
    }

    // 4. Try matching without '+'
    const sortedByDigits = [...normalizedCountries].sort(
      (a, b) => b.countryCode.replace(/[^0-9]/g, "").length - a.countryCode.replace(/[^0-9]/g, "").length
    );
    const matchWithoutPlus = sortedByDigits.find((c) => {
      const cDigits = c.countryCode.replace(/[^0-9]/g, "");
      return digitsOnly.startsWith(cDigits) && digitsOnly.length > cDigits.length;
    });

    if (matchWithoutPlus) {
      setSelectedCountry(matchWithoutPlus);
      const cDigits = matchWithoutPlus.countryCode.replace(/[^0-9]/g, "");
      setNationalNumber(digitsOnly.slice(cDigits.length));
      return;
    }

    // Fallback: keep national number as is
    setNationalNumber(digitsOnly);
  }, [value, normalizedCountries, defaultCountry]);

  // Emit change to parent
  const triggerChange = (country: CountryItem, number: string) => {
    isInternalChange.current = true;
    const cleanNumber = number.replace(/[^0-9]/g, "");
    // If empty national number, value is empty string so 'required' validation works
    const fullNumber = cleanNumber ? `${country.countryCode}${cleanNumber}` : "";

    if (onChange) {
      const syntheticEvent = {
        target: {
          name,
          value: fullNumber,
        },
      };
      (onChange as any)(syntheticEvent);
    }
  };

  const handleCountrySelect = (country: CountryItem) => {
    setSelectedCountry(country);
    setAnchorEl(null);
    setSearchQuery("");
    triggerChange(country, nationalNumber);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;

    // Check if user pasted a full international number with '+'
    if (inputVal.trim().startsWith("+")) {
      const cleanVal = inputVal.trim();
      const sortedByCodeLength = [...normalizedCountries].sort(
        (a, b) => b.countryCode.length - a.countryCode.length
      );
      const match = sortedByCodeLength.find((c) => cleanVal.startsWith(c.countryCode));
      if (match) {
        setSelectedCountry(match);
        const remaining = cleanVal.slice(match.countryCode.length).replace(/[^0-9]/g, "");
        setNationalNumber(remaining);
        triggerChange(match, remaining);
        return;
      }
    }

    // Strip any non-digit characters
    const cleanDigits = inputVal.replace(/[^0-9]/g, "");
    setNationalNumber(cleanDigits);
    triggerChange(selectedCountry, cleanDigits);
  };

  // Filter countries for search dropdown
  const filteredCountries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      const popular = normalizedCountries.filter((c) =>
        POPULAR_COUNTRY_NAMES.includes(c.countryName)
      );
      // Remove duplicates by id
      const popularIds = new Set(popular.map((p) => p.id));
      const rest = normalizedCountries.filter((c) => !popularIds.has(c.id));
      return [...popular, ...rest];
    }
    return normalizedCountries.filter(
      (c) =>
        c.countryName.toLowerCase().includes(query) ||
        c.countryCode.toLowerCase().includes(query)
    );
  }, [normalizedCountries, searchQuery]);

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
      <TextField
        fullWidth={fullWidth}
        size={size}
        name={name}
        value={nationalNumber}
        onChange={handlePhoneChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        helperText={helperText}
        type="tel"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 1 }}>
              <ButtonBase
                disabled={disabled}
                onClick={(e) => !disabled && setAnchorEl(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: "6px",
                  bgcolor:
                    theme.palette.mode === "light"
                      ? "rgba(0,0,0,0.04)"
                      : "rgba(255,255,255,0.06)",
                  "&:hover": {
                    bgcolor:
                      theme.palette.mode === "light"
                        ? "rgba(0,0,0,0.08)"
                        : "rgba(255,255,255,0.12)",
                  },
                  cursor: disabled ? "default" : "pointer",
                }}
              >
                {selectedCountry.flagImg && (
                  <img
                    src={selectedCountry.flagImg}
                    alt={selectedCountry.countryName}
                    style={{
                      width: 22,
                      height: 15,
                      objectFit: "cover",
                      borderRadius: 2,
                      boxShadow: "0 0 1px rgba(0,0,0,0.5)",
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    color: "text.primary",
                  }}
                >
                  {selectedCountry.countryCode}
                </Typography>
                {!disabled && (
                  <ArrowDropDownIcon
                    fontSize="small"
                    sx={{ color: "text.secondary", ml: -0.5 }}
                  />
                )}
              </ButtonBase>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: 24, mx: 1, my: "auto" }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
        }}
      />

      {/* Country Selection Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSearchQuery("");
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 380,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            mt: 0.5,
            p: 1,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ p: 1, pb: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search country or dial code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                fontSize: "0.85rem",
              },
            }}
          />
        </Box>

        <List
          sx={{
            flex: 1,
            overflowY: "auto",
            py: 0.5,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(0,0,0,0.15)",
              borderRadius: 3,
            },
          }}
        >
          {filteredCountries.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 3 }}
            >
              No countries found
            </Typography>
          ) : (
            filteredCountries.map((c) => {
              const isSelected =
                c.countryCode === selectedCountry.countryCode &&
                c.countryName === selectedCountry.countryName;
              return (
                <ListItem key={c.id} disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => handleCountrySelect(c)}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      borderRadius: 1,
                      "&.Mui-selected": {
                        bgcolor: "action.selected",
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <img
                        src={c.flagImg}
                        alt={c.countryName}
                        style={{
                          width: 22,
                          height: 15,
                          objectFit: "cover",
                          borderRadius: 2,
                          boxShadow: "0 0 1px rgba(0,0,0,0.5)",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.85rem",
                              fontWeight: isSelected ? 600 : 400,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: 180,
                            }}
                          >
                            {c.countryName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                              ml: 1,
                            }}
                          >
                            {c.countryCode}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })
          )}
        </List>
      </Popover>
    </Box>
  );
};

export default CountryCodePhoneInput;
